import {
  HttpClient, HttpHeaders, HttpResponse as AngularHttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { apiConfiguration } from '../../../../configurations/api-configuration';
import { AccessService } from '../../../application/services/AccessService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import {
  ImportTicketsResponse,
  SaveEventData,
  SaveEventParticipantData,
  SaveTicketData,
  SportsEvent,
} from '../../types/Event';

@Injectable()
export class EventApiService {
  private readonly eventsUrl = `${apiConfiguration.baseApiUrl}/events`;

  public constructor(
    private http: HttpClient,
    private accessService: AccessService,
  ) { }

  public async getEvents(): Promise<HttpResponse<SportsEvent[]>> {
    return lastValueFrom(this.http.get<HttpResponse<SportsEvent[]>>(this.eventsUrl, { headers: this.getHeaders() }));
  }

  public async getEvent(eventId: string): Promise<HttpResponse<SportsEvent | null>> {
    return lastValueFrom(this.http.get<HttpResponse<SportsEvent | null>>(`${this.eventsUrl}/${eventId}`, { headers: this.getHeaders() }));
  }

  public async createEvent(data: SaveEventData): Promise<HttpResponse<SportsEvent>> {
    return lastValueFrom(this.http.post<HttpResponse<SportsEvent>>(this.eventsUrl, data, { headers: this.getHeaders() }));
  }

  public async updateEvent(eventId: string, data: Partial<SaveEventData>): Promise<HttpResponse<SportsEvent>> {
    return lastValueFrom(this.http.put<HttpResponse<SportsEvent>>(`${this.eventsUrl}/${eventId}`, data, { headers: this.getHeaders() }));
  }

  public async deleteEvent(eventId: string): Promise<HttpResponse<void>> {
    return lastValueFrom(this.http.delete<HttpResponse<void>>(`${this.eventsUrl}/${eventId}`, { headers: this.getHeaders() }));
  }

  public async updateEventParticipants(eventId: string, participants: SaveEventParticipantData[]): Promise<HttpResponse<SportsEvent>> {
    return lastValueFrom(this.http.put<HttpResponse<SportsEvent>>(`${this.eventsUrl}/${eventId}/participants`, { participants }, { headers: this.getHeaders() }));
  }

  public async createTicket(eventId: string, data: SaveTicketData): Promise<HttpResponse<SportsEvent>> {
    return lastValueFrom(this.http.post<HttpResponse<SportsEvent>>(`${this.eventsUrl}/${eventId}/tickets`, data, { headers: this.getHeaders() }));
  }

  public async createTicketsBulk(eventId: string, tickets: SaveTicketData[]): Promise<HttpResponse<ImportTicketsResponse>> {
    return lastValueFrom(this.http.post<HttpResponse<ImportTicketsResponse>>(`${this.eventsUrl}/${eventId}/tickets/bulk`, { tickets }, { headers: this.getHeaders() }));
  }

  public async updateTicket(eventId: string, ticketId: string, data: Partial<SaveTicketData>): Promise<HttpResponse<SportsEvent>> {
    return lastValueFrom(this.http.put<HttpResponse<SportsEvent>>(`${this.eventsUrl}/${eventId}/tickets/${ticketId}`, data, { headers: this.getHeaders() }));
  }

  public async deleteTicket(eventId: string, ticketId: string): Promise<HttpResponse<SportsEvent>> {
    return lastValueFrom(this.http.delete<HttpResponse<SportsEvent>>(`${this.eventsUrl}/${eventId}/tickets/${ticketId}`, { headers: this.getHeaders() }));
  }

  public async downloadTicketFile(eventId: string, ticketId: string): Promise<AngularHttpResponse<Blob>> {
    return lastValueFrom(this.http.get(`${this.eventsUrl}/${encodeURIComponent(eventId)}/tickets/${encodeURIComponent(ticketId)}/file`, {
      headers: this.getHeaders(),
      observe: 'response',
      responseType: 'blob',
    }));
  }

  public async importTickets(eventId: string, csv: string): Promise<HttpResponse<ImportTicketsResponse>> {
    return lastValueFrom(this.http.post<HttpResponse<ImportTicketsResponse>>(`${this.eventsUrl}/${eventId}/tickets/import`, { csv }, { headers: this.getHeaders() }));
  }

  public async exportTickets(eventId: string): Promise<HttpResponse<{ csv: string }>> {
    return lastValueFrom(this.http.get<HttpResponse<{ csv: string }>>(`${this.eventsUrl}/${eventId}/tickets/export`, { headers: this.getHeaders() }));
  }

  private getHeaders(): HttpHeaders {
    const token = this.accessService.getToken();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }
}
